import { Navigation } from 'lucide-react';
import type { BubbleRow } from '@/components/chat/BubbleBlock';
import type { NavAction } from '@/lib/chat/nav-action';
import { navIconForPath } from '@/lib/chat/nav-action';
import type { VoiceJobListRow } from '@/lib/voice/transcript-storage';

/**
 * A message's extras, as rows inside its bubble.
 *
 * 🚨 Its own directive-free module because BOTH chat surfaces call it and
 * neither owns it — a pure helper shared by a `"use client"` component and
 * anything else belongs in neither of them (anti-pattern #18). It also means
 * the panel and the bar cannot describe the same job two different ways, which
 * is the failure this whole redesign exists to close.
 *
 * Nothing here chooses wording. Every visible string arrives already formatted
 * from `job-list-rows.ts` or from the engine, which is the contract that makes
 * the fourth row read exactly like the first.
 */

/**
 * A day's work.
 *
 * Was `JobListCard` — a card of its own, with its own chamfer at a notch
 * nothing else used. The rows are the same rows; they are in the bubble now.
 * The client leads, because on insurance work that is the name an office is
 * chasing and the householder's is not.
 */
export function jobListRows(
  rows: readonly VoiceJobListRow[],
  onOpen: (path: string) => void,
): BubbleRow[] {
  return rows.map(row => ({
    id: row.id,
    title: row.client,
    detail: row.where,
    meta: [row.time, row.type].filter(Boolean).join(' · ') || undefined,
    group: row.group,
    onPress: () => onOpen(row.path),
  }));
}

/**
 * "View Danny Cole" — the engine's go-and-look-at-this buttons.
 *
 * 🚨 These were full-width orange primary buttons, and six of them was a wall
 * of calls-to-action rather than a list — `JobListCard`'s own comment has said
 * so since the day it was written. A destination is a ROW now: orange is
 * reserved for a tap that sends or changes something, and opening a page does
 * neither.
 */
export function navActionRows(
  navActions: readonly NavAction[],
  handlers: { onOpen: (path: string) => void; onDirections: (destination: string) => void },
): BubbleRow[] {
  return navActions.map((nav, i) => ({
    id: `${nav.label}-${i}`,
    title: nav.label,
    icon: nav.directionsTo ? Navigation : navIconForPath(nav.path),
    onPress: () => {
      if (nav.directionsTo) {
        // Hand off to the device's maps app — leave the chat where it is; the
        // maps app comes to the foreground itself.
        handlers.onDirections(nav.directionsTo);
        return;
      }
      handlers.onOpen(nav.path);
    },
  }));
}
